import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { isEmpty } from 'ramda'
import React from 'react'

import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { GetImageProps, GetVoiceProps } from '@/app/module/campaigns/component/item/steps/content/part-config-props'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import { ItemType } from '@/app/module/campaigns/helpers'
import { CampaignTemplateType, Language, LocalAction, PartFamily, SenderIdOption } from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'
import { CountryTimezone, FilesType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

type Props = {
  countriesTimezones: CountryTimezone[]
  countryCode: string
  defaultLanguage: string
  files: FilesType
  id: string
  index: number
  item: ItemType
  languages: Language[]
  orgId: number
  personalizationList: PersonalizationType
  senderIds: SenderIdOption[]
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string
  type: CampaignTemplateType
  whatsappSenderIds: SenderIdOption[]

  changeHandler: (item: ItemType) => void
  getImageProps: GetImageProps
  getVoiceProps: GetVoiceProps
}

export default function CampaignContentQuestionTopup(props: Props) {
  const {
    countriesTimezones,
    countryCode,
    defaultLanguage,
    files,
    id,
    index,
    item,
    languages,
    orgId,
    personalizationList,
    senderIds,
    timezone,
    timezones,
    token,
    type,
    whatsappSenderIds,
    changeHandler,
    getImageProps,
    getVoiceProps,
  } = props

  const [state, setState] = React.useState<{ onSuccess: LocalAction[]; onError: LocalAction[] }>({
    onSuccess: [],
    onError: [],
  })
  const [initialized, setInitialized] = React.useState(false)

  const { successActions, failureActions } = React.useMemo(() => {
    return getActionValues(type)
  }, [type])

  React.useEffect(() => {
    if (item.topup && !isEmpty(item.topup)) {
      setInitialized(true)
      setState(fromProps(item.topup))
    }
  }, [item?.topup])

  const handleTopupActionsChange = (newItem: { onSuccess?: LocalAction[]; onError?: LocalAction[] }) => {
    setState({
      ...state,
      ...newItem,
    })
    const returnToProps = backToProps({
      ...state,
      ...newItem,
    })

    if (initialized) {
      changeHandler({
        ...item,
        topup: {
          ...item.topup,
          ...returnToProps,
        },
      })
    }
  }

  return (
    <div id="topup-block-cont" data-testid="topup-container">
      <Divider
        style={{
          margin: '0rem -24px 1rem',
        }}
      />
      <div>
        <Typography>Top-up Success Actions</Typography>
        <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
          Specify what actions we should take if the top-up succeeds
        </Typography>
        <div>
          <ActionsBox
            actionOptions={successActions}
            actions={state.onSuccess}
            countriesTimezones={countriesTimezones}
            countryCode={countryCode}
            defaultLanguage={defaultLanguage}
            defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.actionsOnly}
            disableLabel
            files={files}
            hasTranscribe={false}
            index={index}
            isCloseDisabled
            languages={languages}
            orgId={orgId}
            personalizationList={personalizationList}
            senderIds={senderIds}
            timezone={timezone}
            timezones={timezones}
            token={token}
            transcribeEnabled={false}
            updateContactConfig={{
              invalid: false,
              openResponse: false,
              question: false,
            }}
            voiceId={`${id}-topup`}
            whatsappSenderIds={whatsappSenderIds}
            onChange={(actionsList: LocalAction[]) =>
              handleTopupActionsChange({
                onSuccess: actionsList,
              })
            }
            getImageProps={getImageProps}
            getVoiceProps={getVoiceProps}
          />
        </div>
      </div>
      <div>
        <Typography>Top-up Failure Actions</Typography>
        <Typography variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
          Specify what actions we should take if the top-up fails
        </Typography>
        <div>
          <ActionsBox
            actionOptions={failureActions}
            actions={state.onError}
            countriesTimezones={countriesTimezones}
            countryCode={countryCode}
            defaultLanguage={defaultLanguage}
            defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.actionsOnly}
            disableLabel
            files={files}
            hasTranscribe={false}
            index={index}
            isCloseDisabled
            languages={languages}
            orgId={orgId}
            personalizationList={personalizationList}
            senderIds={senderIds}
            timezone={timezone}
            timezones={timezones}
            token={token}
            transcribeEnabled={false}
            updateContactConfig={{
              invalid: false,
              openResponse: false,
              question: false,
            }}
            voiceId={`${id}-topup`}
            whatsappSenderIds={whatsappSenderIds}
            onChange={(actionsList: LocalAction[]) =>
              handleTopupActionsChange({
                onError: actionsList,
              })
            }
            getImageProps={getImageProps}
            getVoiceProps={getVoiceProps}
          />
        </div>
      </div>
    </div>
  )
}

const getActionValues = (type: CampaignTemplateType) => {
  const successActions = getActionOptionsForCampaign({
    campaignType: type,
    partType: PartFamily.Topup,
    actionNamespace: ActionContext.TopupSuccess,
  })
  const failureActions = getActionOptionsForCampaign({
    campaignType: type,
    partType: PartFamily.Topup,
    actionNamespace: ActionContext.TopupFailure,
  })

  return {
    successActions,
    failureActions,
  }
}

export const fromProps = ({ onSuccess = [], onError = [] }: { onSuccess?: any[]; onError?: any[] }) => ({
  onSuccess: unwrapActions(onSuccess || []),
  onError: unwrapActions(onError || []),
})

export const backToProps = ({
  onSuccess = [],
  onError = [],
}: {
  onSuccess: LocalAction[]
  onError: LocalAction[]
}) => ({
  onSuccess: wrapActions(onSuccess),
  onError: wrapActions(onError),
})
